import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGroupApi, formatTime, formatDayHeading } from "../groupApi";
import LoadingComp from "../../../components/LoadingComp";
import { GroupAvatar } from "./ui";

const POLL_MS = 4000; // no websocket infrastructure in this backend -> lightweight polling

export default function GroupChat({ groupId, onGone }) {
  const { call, json } = useGroupApi();
  const [messages, setMessages] = useState(null); // null = loading
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const stickRef = useRef(true);   // auto-scroll only while the user is at the bottom
  const lastIdRef = useRef(0);

  const scrollDown = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const merge = useCallback((incoming) => {
    setMessages((prev) => {
      const seen = new Set((prev || []).map((m) => m.message_id));
      const fresh = incoming.filter((m) => !seen.has(m.message_id));
      if (fresh.length === 0 && prev) return prev;
      const all = [...(prev || []), ...fresh];
      lastIdRef.current = all.length ? all[all.length - 1].message_id : 0;
      return all;
    });
  }, []);

  // initial page (newest 30)
  useEffect(() => {
    let ignore = false;
    setMessages(null);
    call(`groups/${groupId}/messages`, { method: "GET" })
      .then((r) => {
        if (ignore) return;
        lastIdRef.current = r.messages.length ? r.messages[r.messages.length - 1].message_id : 0;
        setMessages(r.messages);
        setHasMore(r.has_more);
        stickRef.current = true;
      })
      .catch((e) => {
        if (e.status === 404 && onGone) onGone();
        else if (!ignore) setMessages([]);
      });
    return () => { ignore = true; };
  }, [groupId, call, onGone]);

  // near-real-time updates
  useEffect(() => {
    const id = setInterval(async () => {
      if (document.hidden || messages === null) return;
      try {
        const r = await call(`groups/${groupId}/messages?after_id=${lastIdRef.current}`, { method: "GET" });
        if (r.messages.length) merge(r.messages);
      } catch (e) {
        if (e.status === 404 && onGone) onGone(); // removed from group while viewing
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [groupId, call, merge, messages, onGone]);

  useEffect(() => {
    if (stickRef.current) scrollDown();
  }, [messages]);

  const loadOlder = async () => {
    if (!messages || !messages.length || loadingOlder) return;
    setLoadingOlder(true);
    const el = listRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const r = await call(`groups/${groupId}/messages?before_id=${messages[0].message_id}`, { method: "GET" });
      stickRef.current = false;
      setMessages((prev) => [...r.messages, ...prev]);
      setHasMore(r.has_more);
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight; // keep reading position
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const m = await json(`groups/${groupId}/messages`, "POST", { message: body });
      setText("");
      stickRef.current = true;
      merge([m]);
    } catch (e) {
      if (e.status === 404 && onGone) onGone();
      else toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const onScroll = () => {
    const el = listRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  let lastDay = null;
  return (
    <div className="grpPanel grpChat">
      <div className="grpMessages" ref={listRef} onScroll={onScroll}>
        {messages === null && <LoadingComp />}
        {messages && hasMore && (
          <button type="button" className="grpBtn grpBtn-light grpBtn-sm grpOlder" disabled={loadingOlder} onClick={loadOlder}>
            {loadingOlder ? "در حال بارگذاری..." : "پیام‌های قدیمی‌تر"}
          </button>
        )}
        {messages && messages.length === 0 && (
          <div className="grpEmpty">
            <span className="grpEmptyIcon">💬</span>
            <div className="grpEmptyTitle yekan">گفتگو رو شروع کن</div>
            <div className="yekan caption-1">اولین پیام رو برای اعضای گروه بفرست</div>
          </div>
        )}
        {(messages || []).map((m) => {
          const day = m.created_at.slice(0, 10);
          const showDay = day !== lastDay;
          lastDay = day;
          return (
            <div key={m.message_id} style={{ display: "contents" }}>
              {showDay && <div className="grpOlder caption-1 grey-2 yekan">{formatDayHeading(m.created_at)}</div>}
              <div className={`grpMsg ${m.is_mine ? "grpMsg-mine" : "grpMsg-other"}`}>
                {!m.is_mine && <GroupAvatar name={m.name} size="sm" />}
                <div className="grpBubble">
                  {!m.is_mine && <div className="grpMsgName yekan">{m.name}</div>}
                  <div className="grpMsgText yekan">{m.message}</div>
                  <div className="grpMsgTime yekan">{formatTime(m.created_at)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="grpComposer">
        <textarea
          rows={1}
          className="yekan"
          value={text}
          maxLength={2000}
          placeholder="پیام بنویس..."
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="grpSend" disabled={!text.trim() || sending} onClick={send} aria-label="ارسال">
          <span className="icon-iconsend"></span>
        </button>
      </div>
    </div>
  );
}
